(() => {
  'use strict';

  const CONFIG_ID = 'nxs-market-redirect-config';
  const MODAL_ID = 'nxs-geolocation-modal';
  const STORAGE_KEY = 'nxs_store_redirect_choice';
  const PROMPT_KEY_PREFIX = 'nxs_geo_prompt_seen:';
  const REMEMBER_DAYS = 30;
  const NA_COUNTRIES = new Set(['US', 'CA']);
  const COUNTRY_NAMES = {
    US: 'the United States', CA: 'Canada', IN: 'India', GB: 'the United Kingdom', AU: 'Australia', NZ: 'New Zealand', SG: 'Singapore', AE: 'the United Arab Emirates', QA: 'Qatar', ZA: 'South Africa'
  };

  const readConfig = () => {
    const node = document.getElementById(CONFIG_ID);
    if (!node) return null;
    try { return JSON.parse(node.textContent || '{}'); } catch (error) { return null; }
  };

  const config = readConfig();
  if (!config) return;

  const PROTECTED_PATHS = [
    /^\/cart(?:\/|$)/i, /^\/checkout(?:\/|$)/i, /^\/checkouts(?:\/|$)/i, /^\/account(?:\/|$)/i, /^\/orders?(?:\/|$)/i, /^\/admin(?:\/|$)/i,
  ];
  const normalizeHost = (host) => String(host || '').replace(/^www\./i, '').toLowerCase();
  const currentHost = normalizeHost(window.location.hostname);
  const currentStore = Object.keys(config.stores || {}).find((key) => normalizeHost(config.stores[key].host) === currentHost) || config.currentStore || '';
  const storeForCountry = (countryCode) => (config.countryStoreMap && config.countryStoreMap[String(countryCode || '').toUpperCase()]) || config.defaultStore;
  const isProtectedPath = () => PROTECTED_PATHS.some((pattern) => pattern.test(window.location.pathname));
  const modal = document.getElementById(MODAL_ID);
  if (!modal || !currentStore || isProtectedPath()) return;

  const selectors = {
    dialog: modal.querySelector('[data-nxs-geo-dialog]'),
    title: modal.querySelector('[data-nxs-geo-title]'),
    message: modal.querySelector('[data-nxs-geo-message]'),
    accept: modal.querySelector('[data-nxs-geo-accept]'),
    decline: modal.querySelector('[data-nxs-geo-decline]'),
    close: modal.querySelector('[data-nxs-geo-close]'),
    remember: modal.querySelector('[data-nxs-geo-remember]'),
  };

  const track = (eventName, detail = {}) => {
    const payload = Object.assign({ event: eventName, current_store: currentStore, pathname: window.location.pathname }, detail);
    window.dispatchEvent(new CustomEvent('nxs:analytics', { detail: payload }));
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    if (window.Shopify && typeof window.Shopify.analytics?.publish === 'function') window.Shopify.analytics.publish(eventName, payload);
  };

  const getStoredChoice = () => {
    try {
      const value = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
      if (!value || value.expiresAt < Date.now()) return null;
      return value;
    } catch (error) { return null; }
  };

  const setStoredChoice = (store) => {
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ store, expiresAt: Date.now() + REMEMBER_DAYS * 24 * 60 * 60 * 1000 })); } catch (error) {}
  };

  const normalizeCountryCode = (countryCode) => {
    const code = String(countryCode || '').trim().toUpperCase();
    return /^[A-Z]{2}$/.test(code) ? code : '';
  };

  const getBrowserCountry = () => {
    const languages = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language];
    for (const language of languages) {
      const match = String(language || '').match(/[-_]([A-Z]{2})\b/i);
      const code = normalizeCountryCode(match && match[1]);
      if (code) return code;
    }
    return '';
  };

  const getShopifySuggestedCountry = async () => {
    const shopifyCountry = normalizeCountryCode(window.Shopify && window.Shopify.country);
    if (shopifyCountry) return shopifyCountry;

    try {
      const response = await fetch('/browsing_context_suggestions.json', {
        method: 'GET',
        credentials: 'same-origin',
      });
      if (!response.ok) return '';

      const data = await response.json();
      return normalizeCountryCode(data?.detected_values?.country_code);
    } catch (error) {
      return '';
    }
  };

  const getDetectedCountry = async () => {
    return (
      await getShopifySuggestedCountry() ||
      normalizeCountryCode(config.localization?.countryIso) ||
      getBrowserCountry()
    );
  };

  const countryName = (code) => config.countryNames?.[code] || COUNTRY_NAMES[code] || code;

  const markPromptSeen = (country, destinationStore) => {
    try { window.sessionStorage.setItem(`${PROMPT_KEY_PREFIX}${country}:${destinationStore}`, '1'); } catch (error) {}
  };

  const wasPromptSeen = (country, destinationStore) => {
    try { return window.sessionStorage.getItem(`${PROMPT_KEY_PREFIX}${country}:${destinationStore}`) === '1'; } catch (error) { return false; }
  };

  const getFocusable = () => Array.from(modal.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'));
  let previouslyFocused = null;
  let promptContext = null;

  const closeModal = (declined = true) => {
    modal.hidden = true;
    document.documentElement.classList.remove('nxs-geo-modal-open');
    document.removeEventListener('keydown', onKeydown);
    if (declined && promptContext) {
      if (selectors.remember?.checked) setStoredChoice(currentStore);
      track('geo_redirect_decline', promptContext.analytics);
    }
    if (previouslyFocused && typeof previouslyFocused.focus === 'function') previouslyFocused.focus();
  };

  function onKeydown(event) {
    if (event.key === 'Escape') { event.preventDefault(); closeModal(true); return; }
    if (event.key !== 'Tab') return;
    const focusable = getFocusable();
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  const openModal = (context) => {
    promptContext = context;
    previouslyFocused = document.activeElement;
    selectors.title.textContent = context.title;
    selectors.message.textContent = context.message;
    (selectors.accept.querySelector('span') || selectors.accept).textContent = context.acceptLabel;
    if (selectors.remember) selectors.remember.checked = false;
    modal.hidden = false;
    document.documentElement.classList.add('nxs-geo-modal-open');
    document.addEventListener('keydown', onKeydown);
    markPromptSeen(context.country, context.destinationStore);
    track('geo_prompt_shown', context.analytics);
    window.setTimeout(() => selectors.accept.focus(), 0);
  };

  const buildPromptContext = ({ country, destinationStore, analytics = {}, eventName = 'geo_redirect_accept' }) => {
    const isNorthAmerica = NA_COUNTRIES.has(country);
    const destinationLabel = isNorthAmerica ? 'North America Store' : 'International Store';

    return {
      country,
      destinationStore,
      eventName,
      analytics: Object.assign({
        visitor_country: country,
        destination_store: destinationStore,
        destination_store_host: config.stores?.[destinationStore]?.host,
      }, analytics),
      title: 'Switch store?',
      message: isNorthAmerica
        ? `It looks like you're visiting from ${countryName(country)}. Would you like to shop on our North America store?`
        : `It looks like you're visiting from ${countryName(country)}. Would you like to continue shopping on our International Store?`,
      acceptLabel: `Go to ${destinationLabel}`,
    };
  };

  const openStorePrompt = (options) => {
    openModal(buildPromptContext(options));
  };

  const init = async () => {
    const storedChoice = getStoredChoice();
    if (storedChoice) return;

    const country = await getDetectedCountry();
    if (!country) return;

    const destinationStore = storeForCountry(country);
    if (!destinationStore || destinationStore === currentStore || wasPromptSeen(country, destinationStore)) return;

    openStorePrompt({ country, destinationStore });
  };

  selectors.accept?.addEventListener('click', () => {
    if (!promptContext) return;
    window.dispatchEvent(new CustomEvent('nxs:redirect-to-store', {
      detail: { store: promptContext.destinationStore, remember: !!selectors.remember?.checked, eventName: promptContext.eventName || 'geo_redirect_accept', analytics: promptContext.analytics }
    }));
  });

  window.addEventListener('nxs:prompt-store-redirect', (event) => {
    const detail = event.detail || {};
    const country = String(detail.country || '').toUpperCase();
    const destinationStore = detail.store || storeForCountry(country);
    if (!country || !destinationStore || destinationStore === currentStore || isProtectedPath()) return;

    event.preventDefault();
    openStorePrompt({
      country,
      destinationStore,
      eventName: detail.eventName || 'country_selector_redirect',
      analytics: Object.assign({ selected_country: country, prompt_source: detail.source || 'country_selector' }, detail.analytics || {}),
    });
  });
  selectors.decline?.addEventListener('click', () => closeModal(true));
  selectors.close?.addEventListener('click', () => closeModal(true));
  modal.addEventListener('click', (event) => { if (event.target === modal) closeModal(true); });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true }); else init();
})();
